// Zeitnachweis als eigenes PDF — das Dokument, das der Kunde bekommt.
//
// Bewusst getrennt vom Ticket-Export: dort steht der komplette Vorgang samt interner
// Kommentare und Historie. Hier stehen nur die abrechenbaren Zeiten, damit ein Nachweis
// verschickt werden kann, ohne dass jemand vorher etwas herausschneiden muss.
import jsPDF from 'jspdf';
import { nachweisSummeMinuten, nachweisZeilen } from '../zeiterfassung/zeitnachweis';
import { stundenText } from '../zeiterfassung/berechnung';
import type { TicketZeit } from '../zeiterfassung/ticketzeit';

const COLORS = {
  dark: [15, 23, 42] as [number, number, number],
  gold: [196, 167, 125] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  gray: [156, 163, 175] as [number, number, number],
  text: [55, 65, 81] as [number, number, number],
};

export interface ZeitnachweisTicket {
  ticket_nummer: string;
  titel: string;
  objekt?: { bezeichnung?: string | null; strasse?: string | null; hausnummer?: string | null } | null;
  einheit?: string | null;
}

const datumKurz = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
const uhr = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
const dauerText = (beginn: string, ende: string | null) =>
  stundenText(Math.max(0, Math.round(((ende ? Date.parse(ende) : 0) - Date.parse(beginn)) / 60000))).replace(' h', '');

/**
 * Erzeugt den Nachweis und startet den Download. Gibt `false` zurück, wenn es nichts zu
 * bescheinigen gibt — der Aufrufer meldet das dann, statt ein leeres Blatt auszugeben.
 */
export function exportZeitnachweisPdf(
  ticket: ZeitnachweisTicket,
  zeiten: TicketZeit[],
  namen: Record<string, string> = {},
): boolean {
  const zeilen = nachweisZeilen(zeiten);
  if (zeilen.length === 0) return false;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      pdf.addPage();
      y = margin;
    }
  };

  // ── Kopf (gleiche Handschrift wie der Ticket-Export) ──
  pdf.setFillColor(...COLORS.dark);
  pdf.rect(0, 0, pageWidth, 38, 'F');
  pdf.setFillColor(...COLORS.gold);
  pdf.rect(0, 38, pageWidth, 1.5, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(...COLORS.white);
  pdf.text('Zeitnachweis', margin, 16);

  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.gold);
  pdf.text(`${ticket.ticket_nummer} · ${ticket.titel}`.substring(0, 80), margin, 25);

  pdf.setFontSize(7);
  pdf.setTextColor(180, 180, 180);
  pdf.text(
    `Erstellt am ${new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`,
    margin, 34,
  );

  y = 48;

  // ── Objekt / Einheit ──
  const objektText = ticket.objekt
    ? [ticket.objekt.bezeichnung, [ticket.objekt.strasse, ticket.objekt.hausnummer].filter(Boolean).join(' ')]
        .filter(Boolean).join(' · ')
    : '–';
  const feld = (label: string, wert: string, x: number) => {
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.gray);
    pdf.text(label.toUpperCase(), x, y);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.text);
    pdf.text(wert || '–', x, y + 5);
  };
  feld('Objekt', objektText, margin);
  feld('Einheit', ticket.einheit || '–', margin + contentWidth / 2);
  y += 16;

  pdf.setDrawColor(230, 230, 230);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 8;

  // ── Tabelle ──
  const spalten = {
    datum: margin,
    zeitraum: margin + 22,
    dauer: margin + 52,
    taetigkeit: margin + 70,
    mitarbeiter: pageWidth - margin,
  };

  const kopfzeile = () => {
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.gray);
    pdf.text('DATUM', spalten.datum, y);
    pdf.text('ZEITRAUM', spalten.zeitraum, y);
    pdf.text('DAUER', spalten.dauer, y);
    pdf.text('TÄTIGKEIT', spalten.taetigkeit, y);
    pdf.text('MITARBEITER', spalten.mitarbeiter, y, { align: 'right' });
    y += 3;
    pdf.setDrawColor(230, 230, 230);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 5;
  };
  kopfzeile();

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  // Zeilenabstand der Tätigkeit bei 8,5 pt: 8,5 × 0,3528 mm × 1,2 ≈ 3,6 mm.
  const ZEILENHOEHE = 3.6;
  for (const z of zeilen) {
    // Tätigkeit VOLLSTÄNDIG, umbrochen auf die Spaltenbreite (Ticket #500, Björn): bisher
    // stand hier `[0]` — nur die erste umbrochene Zeile kam aufs Blatt, der Rest fiel weg.
    // Die Zeile wird so hoch wie ihr Text; Datum, Zeitraum, Dauer und Name stehen oben.
    const platz = spalten.mitarbeiter - spalten.taetigkeit - 32;
    const textZeilen = pdf.splitTextToSize(z.taetigkeit || '–', platz) as string[];
    const hoehe = Math.max(6, textZeilen.length * ZEILENHOEHE + 2.4);
    if (y + hoehe > pageHeight - 20) { pdf.addPage(); y = margin; kopfzeile(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); }
    pdf.setTextColor(...COLORS.text);
    pdf.text(datumKurz(z.beginn), spalten.datum, y);
    pdf.text(`${uhr(z.beginn)}–${uhr(z.ende)}`, spalten.zeitraum, y);
    pdf.setFont('helvetica', 'bold');
    pdf.text(dauerText(z.beginn, z.ende), spalten.dauer, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(textZeilen, spalten.taetigkeit, y, { lineHeightFactor: 1.2 });
    pdf.setTextColor(...COLORS.gray);
    pdf.text(namen[z.mitarbeiter_id] || '', spalten.mitarbeiter, y, { align: 'right' });
    y += hoehe;
  }

  // ── Summe ──
  checkPage(18);
  y += 2;
  pdf.setDrawColor(...COLORS.dark);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 7;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.dark);
  pdf.text('Summe abrechenbar', spalten.taetigkeit, y);
  pdf.text(`${stundenText(nachweisSummeMinuten(zeiten))}`, spalten.mitarbeiter, y, { align: 'right' });

  const heute = new Date().toISOString().slice(0, 10);
  pdf.save(`Zeitnachweis_${ticket.ticket_nummer}_${heute}.pdf`);
  return true;
}
